import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Group, Rect } from 'react-konva';
import type { SceneContext } from 'konva/lib/Context';
import { connect } from 'react-redux';
import { translateCanvasPoints } from 'src/util/misc';
import type { RootState } from 'src/store';
import Border from './border';
import GrabPoints from './grabPoints';
import getCroppingRule from '../selectors/getCroppingRule';
import getSceneVariation from '../selectors/getSceneVariation';
import { saveCropping, setCroppingArea } from '../slice';

interface Point {
    x: number;
    y: number;
}

interface Props {
    canvasHeight: number;
    canvasWidth: number;
    savePointsState: (points: Point[]) => void;
    croppingPoints: { x0: number; y0: number; x1: number; y1: number };
    cropEnable: boolean;
    offset: Point;
    saveCroppingState: (data: { points: Point[]; scale: number }) => void;
    scale: number;
    image: { width: number; height: number };
    constraint: { enable: boolean; width: number; height: number };
}

const OFFSET = 1;
const COLOR = '#b1b1b1';

function usePrevious<T>(value: T): T | undefined {
    const prev = useRef<T>();
    useEffect(() => {
        prev.current = value;
    }, [value]);
    return prev.current;
}

function Cropping(props: Props) {
    const {
        canvasHeight,
        canvasWidth,
        savePointsState,
        croppingPoints,
        cropEnable,
        offset,
        saveCroppingState,
        scale,
        image,
        constraint,
    } = props;
    const points = useMemo(
        () => [
            { x: croppingPoints.x0, y: croppingPoints.y0 },
            { x: croppingPoints.x1, y: croppingPoints.y1 },
        ],
        [croppingPoints],
    );
    const [localPoints, setLocalPoints] = useState(points);
    const prevData = usePrevious({ scale, offset });
    const prevOrigin = usePrevious({ scale, offset, canvasHeight, canvasWidth });
    const constraintFactor = constraint.enable ? constraint.width / constraint.height : 1;

    useEffect(() => {
        // scale points to match display size
        const offsetX = Math.max(offset.x, 0);
        const offsetY = Math.max(offset.y, 0);

        const initialData = [
            { x: offsetX, y: offsetY },
            { x: canvasWidth - offsetX, y: canvasHeight - offsetY },
        ];

        if (JSON.stringify({ scale, offset, canvasHeight, canvasWidth }) !== JSON.stringify(prevOrigin)) {
            saveCroppingState({ points: initialData, scale });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scale, offset, canvasHeight, canvasWidth]);

    useEffect(() => {
        if (prevData && scale !== prevData.scale) {
            const newPoints = translateCanvasPoints({
                points: localPoints,
                scale: prevData.scale,
                offset: prevData.offset,
                target: { scale, offset },
            });
            savePointsState(newPoints); // save points to state
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scale, offset, canvasHeight, canvasWidth]);

    useEffect(() => {
        // `localPoints` is intentionally excluded from the deps array -- including it (as the
        // react-hooks/exhaustive-deps rule initially suggested here) reruns this effect on every
        // drag step, since `onDragMove` below updates `localPoints` directly, and this effect would
        // then immediately revert it back to the stable `points` prop on every single mousemove,
        // producing a flash-to-new-position-then-snap-back on every drag step. This should only
        // sync `localPoints` when the Redux-derived `points` prop itself changes, matching the
        // original class component's equivalent effect (which only watched `points`).
        if (JSON.stringify(localPoints) !== JSON.stringify(points)) {
            setLocalPoints(points);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [points]);

    const onDragStart = (_curPoints?: Point) => {};

    const onDragMove = (curPoints: Point[]) => {
        if (constraint.enable) {
            const height = (curPoints[1].x - curPoints[0].x) / constraintFactor;
            curPoints[1].y = curPoints[0].y + height;
        }
        setLocalPoints(curPoints);
    };

    const onDragEnd = (curPoints: Point[]) => {
        savePointsState(curPoints); // save points to state
    };

    const clip = (ctx: SceneContext) => {
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(canvasWidth + OFFSET * 2, 0);
        ctx.lineTo(canvasWidth + OFFSET * 2, canvasHeight + OFFSET * 2);
        ctx.lineTo(0, canvasHeight + OFFSET * 2);
        ctx.moveTo(localPoints[0].x, localPoints[1].y);
        ctx.lineTo(localPoints[1].x, localPoints[1].y);
        ctx.lineTo(localPoints[1].x, localPoints[0].y);
        ctx.lineTo(localPoints[0].x, localPoints[0].y);
        ctx.closePath();
    };

    const fixX = localPoints[0].x === localPoints[1].x ? 20 : 0;
    const fixY = localPoints[0].y === localPoints[1].y ? 20 : 0;

    // Calculate the points, restricting the points into the canvas area and avoiding situations where the position of 2 or more points are in the same location
    // ie: if P1 and P3 are in the same position, there will be a line and won't be able to grab a point, same happens with P2 and P4
    const rectanglePoints = useMemo(
        () => [
            {
                x: Math.min(Math.max(localPoints[0].x, 10), canvasWidth),
                y: Math.min(Math.max(localPoints[0].y, 10), canvasHeight),
            },
            {
                x: Math.min(Math.max(+localPoints[1].x + fixX, 50), canvasWidth),
                y: Math.min(Math.max(localPoints[0].y, 10), canvasHeight),
            },
            {
                x: Math.min(Math.max(+localPoints[1].x + fixX, 10), canvasWidth),
                y: Math.min(Math.max(+localPoints[1].y + fixY, 50), canvasHeight),
            },
            {
                x: Math.min(Math.max(localPoints[0].x, 50), canvasWidth),
                y: Math.min(Math.max(+localPoints[1].y + fixY, 50), canvasHeight),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [localPoints],
    );

    const borderPoints = useMemo(
        () => [
            {
                x: Math.min(Math.max(localPoints[0].x, 10), canvasWidth),
                y: Math.min(Math.max(localPoints[0].y, 10), canvasHeight),
            },
            {
                x: Math.min(Math.max(+localPoints[1].x + fixX, 50), canvasWidth),
                y: Math.min(Math.max(+localPoints[1].y + fixY, 50), canvasHeight),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [localPoints],
    );

    if (!cropEnable || localPoints.length === 0 || localPoints[0].x === undefined) {
        return <></>;
    }

    return (
        <Layer width={canvasWidth + OFFSET} height={canvasHeight + OFFSET}>
            <Group>
                <Group clipFunc={clip}>
                    <Rect
                        x={offset.x}
                        y={offset.y}
                        width={image.width * scale}
                        height={image.height * scale}
                        fill={'black'}
                        opacity={0.7}
                    />
                </Group>
                <Border
                    points={borderPoints}
                    rectanglePoints={rectanglePoints}
                    onDragStart={onDragStart}
                    onDragMove={onDragMove}
                    onDragEnd={onDragEnd}
                    width={image.width * scale}
                    height={image.height * scale}
                    offset={offset}
                    color={COLOR}
                />
                <Group>
                    <GrabPoints
                        points={rectanglePoints}
                        onDragStart={onDragStart}
                        onDragMove={onDragMove}
                        onDragEnd={onDragEnd}
                        width={canvasWidth}
                        height={canvasHeight}
                        offset={OFFSET}
                        color={COLOR}
                    />
                </Group>
            </Group>
        </Layer>
    );
}

const mapStateToProps = (state: RootState) => {
    const sceneVariation = getSceneVariation(state);

    return {
        cropEnable: !!sceneVariation.cropping?.enable,
        // `cropping.constraint` is always set by this slice's initial state; see the comment on
        // SceneVariationDataLinkType['cropping'] for why that's not reflected by its optional type.
        constraint: sceneVariation.cropping?.constraint ?? { enable: false, width: 100, height: 100 },
        // `getCroppingRule` falls back to `{}` for a not-yet-found rule, which can't actually happen
        // once cropping is enabled (same reasoning as `getRulerById`'s equivalent fallback) --
        // asserting the real shape here instead of leaving it a loose/empty union.
        croppingPoints: getCroppingRule(state) as { x0: number; y0: number; x1: number; y1: number },
    };
};

const croppingContainer = connect(mapStateToProps, {
    savePointsState: saveCropping,
    saveCroppingState: setCroppingArea,
})(Cropping);

export default croppingContainer;
